import { useState, useCallback } from 'react';
import { useQuery, useMutation } from 'react-query';
import sectionService from '../services/sectionService';
import useNotification from './useNotification';

/**
 * Custom hook for section and task management
 * Provides methods and state for sections/tasks using React Query
 * 
 * @param {Object} options - Configuration options
 * @param {boolean} options.activeOnly - Whether to only fetch active sections/tasks
 * @returns {Object} Section and task state and methods
 */
const useSection = ({ 
  activeOnly = true
} = {}) => {
  const { showNotification } = useNotification();
  
  // Get all sections with their tasks
  const {
    data: sectionsData,
    isLoading: isLoadingSections,
    error: sectionsError,
    refetch: refetchSections
  } = useQuery(
    ['sections', { active: activeOnly }],
    () => {
      console.log('useSection: Fetching sections with activeOnly:', activeOnly);
      return sectionService.getSections({ active: activeOnly });
    },
    {
      refetchOnWindowFocus: false,
      retry: 3, // Retry 3 times if there's an error
      staleTime: 60000, // Consider data fresh for 1 minute
      onSuccess: (data) => {
        console.log('useSection: Successfully loaded sections data:', data);
        console.log('useSection: Number of sections:', data?.data?.length || 0);
      },
      onError: (error) => {
        console.error('useSection: Error loading sections:', error);
        console.error('useSection: Error details:', {
          message: error.message,
          status: error.status,
          response: error.response
        });
        // Only show notification for real errors, not network issues
        if (error.message !== 'Network Error') {
          showNotification({ 
            message: `Failed to load sections: ${error.message}`, 
            type: 'error' 
          });
        }
      }
    }
  );
  
  // Initialize sections and tasks (admin only)
  const initializeMutation = useMutation(
    () => sectionService.initializeSectionsAndTasks(),
    {
      onSuccess: () => {
        refetchSections();
        showNotification({ 
          message: 'Sections and tasks initialized successfully', 
          type: 'success' 
        });
      },
      onError: (error) => {
        console.error('Error initializing sections and tasks:', error);
        showNotification({ 
          message: `Failed to initialize sections and tasks: ${error.message}`, 
          type: 'error' 
        });
      }
    }
  );
  
  // Update section roles and active status
  const updateRolesMutation = useMutation(
    (sectionUpdates) => sectionService.updateSectionRoles(sectionUpdates),
    {
      onSuccess: () => {
        refetchSections();
        showNotification({ 
          message: 'Section role settings updated successfully', 
          type: 'success' 
        });
      },
      onError: (error) => {
        console.error('Error updating section roles:', error);
        showNotification({ 
          message: `Failed to update section roles: ${error.message}`, 
          type: 'error' 
        });
      }
    }
  );
  
  // Get section by ID
  const getSectionById = useCallback(async (sectionId) => {
    try {
      const response = await sectionService.getSectionById(sectionId);
      return response.data;
    } catch (error) {
      console.error(`Error fetching section ${sectionId}:`, error);
      showNotification({
        message: `Failed to fetch section: ${error.message}`,
        type: 'error'
      });
      throw error;
    }
  }, [showNotification]);
  
  // Get tasks for a section
  const getSectionTasks = useCallback(async (sectionId) => {
    try {
      const response = await sectionService.getSectionTasks(sectionId);
      return response.data;
    } catch (error) {
      console.error(`Error fetching tasks for section ${sectionId}:`, error);
      showNotification({
        message: `Failed to fetch section tasks: ${error.message}`,
        type: 'error'
      });
      throw error;
    }
  }, [showNotification]);
  
  // Helper function to get a flat list of all tasks
  const getAllTasks = useCallback(() => {
    if (!sectionsData?.data) return [];
    
    const allTasks = [];
    sectionsData.data.forEach(section => {
      if (section.tasks && section.tasks.length > 0) {
        allTasks.push(...section.tasks);
      }
    });
    
    return allTasks;
  }, [sectionsData]);
  
  // Helper function to get all task IDs organized by section
  const getTaskIdsBySection = useCallback(() => {
    if (!sectionsData?.data) return {};
    
    const taskIds = {};
    sectionsData.data.forEach(section => {
      if (section.tasks && section.tasks.length > 0) {
        taskIds[section._id] = section.tasks.map(task => task.id);
      } else {
        taskIds[section._id] = [];
      }
    });
    
    return taskIds;
  }, [sectionsData]);
  
  // Helper function to get active sections based on user roles
  const getActiveSectionsForRole = useCallback((userRole) => {
    if (!sectionsData?.data || !userRole) return [];
    
    return sectionsData.data.filter(section => {
      // Admin can access all active sections
      if (userRole === 'admin') return section.isActive;
      
      // Check if the section is active and the user role is in the required roles
      return (
        section.isActive && 
        (!section.requiredRoles || 
         section.requiredRoles.length === 0 || 
         section.requiredRoles.includes(userRole))
      );
    });
  }, [sectionsData]);

  return {
    // Sections data
    sections: sectionsData?.data || [],
    isLoadingSections,
    sectionsError,
    refetchSections,
    
    // Tasks data
    getAllTasks,
    getTaskIdsBySection,
    getSectionById,
    getSectionTasks,
    getActiveSectionsForRole,
    
    // Role management
    updateSectionRoles: updateRolesMutation.mutateAsync,
    isUpdatingRoles: updateRolesMutation.isLoading,
    
    // Admin operations
    initializeSections: initializeMutation.mutateAsync,
    isInitializing: initializeMutation.isLoading
  };
};

export default useSection;